import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import updateLocale from 'dayjs/plugin/updateLocale';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import {
    Badge,
    Button,
    Calendar,
    Card,
    ConfigProvider,
    DatePicker,
    Empty,
    Form,
    Input,
    List,
    Modal,
    Popconfirm,
    Radio,
    Select,
    Space,
    Switch,
    Tag,
    Typography,
    message
} from 'antd';
import ruRU from 'antd/locale/ru_RU';
import {
    CalendarOutlined,
    DeleteOutlined,
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import {
    canAtLeast,
    getCachedPageAccess
} from '../utils/pageAccess';

dayjs.extend(updateLocale);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.updateLocale('ru', { weekStart: 1 });
dayjs.locale('ru');

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const currentUser = () => {
    try {
        return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
        return null;
    }
};

const TYPE_COLORS = {
    meeting: 'blue',
    event: 'green'
};

const formatWhen = item => {
    const start = dayjs(item.starts_at);
    const end = dayjs(item.ends_at);
    if (item.all_day) {
        if (start.isSame(end, 'day')) {
            return start.format('DD.MM.YYYY') + ' · весь день';
        }
        return `${start.format('DD.MM.YYYY')} — ${end.format('DD.MM.YYYY')}`;
    }
    if (start.isSame(end, 'day')) {
        return `${start.format('DD.MM.YYYY HH:mm')} — ${end.format('HH:mm')}`;
    }
    return `${start.format('DD.MM.YYYY HH:mm')} — ${end.format('DD.MM.YYYY HH:mm')}`;
};

const CalendarPage = () => {
    const me = currentUser();
    const canUse = canAtLeast(
        getCachedPageAccess()?.calendar,
        'view'
    ) || Number(me?.role_id) === 1;
    const [month, setMonth] = useState(dayjs());
    const [selected, setSelected] = useState(dayjs());
    const [items, setItems] = useState([]);
    const [colleagues, setColleagues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [production, setProduction] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();
    const allDay = Form.useWatch('all_day', form);

    const load = useCallback(async (m = month) => {
        setLoading(true);
        try {
            const from = m.startOf('month').subtract(7, 'day');
            const to = m.endOf('month').add(7, 'day');
            const [eventsRes, prodRes] = await Promise.all([
                axios.get('/api/calendar', {
                    headers: authHeaders(),
                    params: {
                        from: from.toISOString(),
                        to: to.toISOString()
                    }
                }),
                axios.get('/api/calendar/production', {
                    headers: authHeaders(),
                    params: {
                        from: from.format('YYYY-MM-DD'),
                        to: to.format('YYYY-MM-DD')
                    }
                })
            ]);
            setItems(eventsRes.data.items || []);
            setProduction(prodRes.data.days || {});
        } catch (error) {
            message.error(
                error.response?.data?.error || 'Не удалось загрузить календарь'
            );
        } finally {
            setLoading(false);
        }
    }, [month]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        axios
            .get('/api/calendar/colleagues', { headers: authHeaders() })
            .then(({ data }) => setColleagues(data.items || []))
            .catch(() => setColleagues([]));
    }, []);

    const byDay = useMemo(() => {
        const map = {};
        for (const item of items) {
            let cursor = dayjs(item.starts_at).startOf('day');
            const last = dayjs(item.ends_at).startOf('day');
            while (cursor.isBefore(last) || cursor.isSame(last, 'day')) {
                const key = cursor.format('YYYY-MM-DD');
                if (!map[key]) map[key] = [];
                map[key].push(item);
                cursor = cursor.add(1, 'day');
                if (map[key].length > 20) break;
            }
        }
        return map;
    }, [items]);

    const dayItems = byDay[selected.format('YYYY-MM-DD')] || [];

    const dayKind = date => {
        const meta = production[date.format('YYYY-MM-DD')];
        if (meta?.kind) return meta;
        const wd = date.day();
        if (wd === 0 || wd === 6) {
            return { kind: 'weekend', label: wd === 0 ? 'Воскресенье' : 'Суббота' };
        }
        return { kind: 'work', label: 'Рабочий день' };
    };

    const openCreate = (day = selected) => {
        setEditing(null);
        form.resetFields();
        const start = day.hour(10).minute(0).second(0);
        form.setFieldsValue({
            type: 'meeting',
            all_day: false,
            range: [start, start.add(1, 'hour')],
            participant_ids: []
        });
        setModalOpen(true);
    };

    const openEdit = item => {
        setEditing(item);
        const start = dayjs(item.starts_at);
        const end = dayjs(item.ends_at);
        form.setFieldsValue({
            title: item.title,
            type: item.type,
            all_day: Boolean(item.all_day),
            range: [start, end],
            location: item.location,
            description: item.description,
            participant_ids: item.participant_ids || []
        });
        setModalOpen(true);
    };

    const save = async values => {
        const [start, end] = values.range || [];
        if (!start || !end) {
            message.error('Укажите время');
            return;
        }
        const payload = {
            title: values.title,
            type: values.type,
            all_day: Boolean(values.all_day),
            starts_at: values.all_day
                ? start.startOf('day').toISOString()
                : start.toISOString(),
            ends_at: values.all_day
                ? end.endOf('day').toISOString()
                : end.toISOString(),
            location: values.location || null,
            description: values.description || null,
            participant_ids: values.participant_ids || []
        };
        try {
            if (editing) {
                await axios.put(`/api/calendar/${editing.id}`, payload, {
                    headers: authHeaders()
                });
                message.success('Сохранено');
            } else {
                await axios.post('/api/calendar', payload, {
                    headers: authHeaders()
                });
                message.success('Добавлено');
            }
            setModalOpen(false);
            await load();
        } catch (error) {
            message.error(
                error.response?.data?.error || 'Не удалось сохранить'
            );
        }
    };

    const remove = async id => {
        try {
            await axios.delete(`/api/calendar/${id}`, {
                headers: authHeaders()
            });
            message.success('Удалено');
            await load();
        } catch (error) {
            message.error(
                error.response?.data?.error || 'Не удалось удалить'
            );
        }
    };

    const canChange = item =>
        canUse && Number(me?.id) === Number(item.created_by);

    return (
        <Layout>
            <ConfigProvider locale={ruRU}>
            <Space
                direction="vertical"
                size="large"
                style={{ width: '100%' }}
            >
                <Space
                    align="center"
                    style={{
                        width: '100%',
                        justifyContent: 'space-between'
                    }}
                    wrap
                >
                    <Space>
                        <CalendarOutlined />
                        <Title level={3} style={{ margin: 0 }}>
                            Календарь
                        </Title>
                    </Space>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => load()}
                            loading={loading}
                        >
                            Обновить
                        </Button>
                        {canUse ? (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => openCreate()}
                            >
                                Добавить
                            </Button>
                        ) : null}
                    </Space>
                </Space>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns:
                            'minmax(0, 1.4fr) minmax(280px, 0.8fr)',
                        gap: 16
                    }}
                    className="calendar-grid"
                >
                    <Card className="crm-calendar">
                        <Calendar
                            value={selected}
                            onSelect={day => {
                                setSelected(day);
                            }}
                            onPanelChange={m => {
                                setMonth(m);
                                setSelected(m);
                            }}
                            fullCellRender={(date, info) => {
                                if (info.type !== 'date') {
                                    return info.originNode;
                                }
                                const meta = dayKind(date);
                                const list =
                                    byDay[date.format('YYYY-MM-DD')] || [];
                                return (
                                    <div
                                        className={`ant-picker-cell-inner ant-picker-calendar-date crm-cal-${meta.kind}`}
                                    >
                                        <div className="ant-picker-calendar-date-value">
                                            {date.date()}
                                        </div>
                                        <div className="ant-picker-calendar-date-content">
                                            {meta.short_label ? (
                                                <div className="crm-cal-chip">
                                                    {meta.short_label}
                                                </div>
                                            ) : null}
                                            <ul
                                                style={{
                                                    listStyle: 'none',
                                                    margin: 0,
                                                    padding: 0
                                                }}
                                            >
                                                {list.slice(0, 3).map(item => (
                                                    <li key={item.id}>
                                                        <Badge
                                                            color={
                                                                item.type ===
                                                                'meeting'
                                                                    ? '#1677ff'
                                                                    : '#52c41a'
                                                            }
                                                            text={
                                                                <Text
                                                                    ellipsis
                                                                    style={{
                                                                        maxWidth: 90
                                                                    }}
                                                                >
                                                                    {item.all_day
                                                                        ? item.title
                                                                        : `${dayjs(item.starts_at).format('HH:mm')} ${item.title}`}
                                                                </Text>
                                                            }
                                                        />
                                                    </li>
                                                ))}
                                                {list.length > 3 ? (
                                                    <li>
                                                        <Text type="secondary">
                                                            +{list.length - 3}
                                                        </Text>
                                                    </li>
                                                ) : null}
                                            </ul>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        <Space wrap size="small" style={{ marginTop: 8 }}>
                            <Tag color="default">Рабочий</Tag>
                            <Tag color="red">Выходной / праздник</Tag>
                            <Tag color="orange">Сокращённый</Tag>
                            <Tag color="green">Рабочий выходной</Tag>
                        </Space>
                    </Card>

                    <Card
                        title={
                            <Space direction="vertical" size={0}>
                                <span>{selected.format('D MMMM YYYY')}</span>
                                {dayKind(selected)?.label ? (
                                    <Text
                                        type="secondary"
                                        style={{ fontWeight: 400, fontSize: 13 }}
                                    >
                                        {dayKind(selected).label}
                                    </Text>
                                ) : null}
                            </Space>
                        }
                        extra={
                            canUse ? (
                                <Button
                                    size="small"
                                    type="link"
                                    onClick={() => openCreate(selected)}
                                >
                                    Добавить
                                </Button>
                            ) : null
                        }
                    >
                        {dayItems.length ? (
                            <List
                                dataSource={dayItems}
                                renderItem={item => (
                                    <List.Item
                                        actions={
                                            canChange(item)
                                                ? [
                                                      <Button
                                                          key="edit"
                                                          type="link"
                                                          size="small"
                                                          onClick={() =>
                                                              openEdit(item)
                                                          }
                                                      >
                                                          Изменить
                                                      </Button>,
                                                      <Popconfirm
                                                          key="del"
                                                          title="Удалить событие?"
                                                          onConfirm={() =>
                                                              remove(item.id)
                                                          }
                                                      >
                                                          <Button
                                                              type="link"
                                                              size="small"
                                                              danger
                                                              icon={
                                                                  <DeleteOutlined />
                                                              }
                                                          />
                                                      </Popconfirm>
                                                  ]
                                                : []
                                        }
                                    >
                                        <List.Item.Meta
                                            title={
                                                <Space wrap>
                                                    <Tag
                                                        color={
                                                            TYPE_COLORS[
                                                                item.type
                                                            ] || 'default'
                                                        }
                                                    >
                                                        {item.type_name ||
                                                            item.type}
                                                    </Tag>
                                                    {item.title}
                                                </Space>
                                            }
                                            description={
                                                <Space
                                                    direction="vertical"
                                                    size={0}
                                                >
                                                    <Text type="secondary">
                                                        {formatWhen(item)}
                                                    </Text>
                                                    {item.location ? (
                                                        <Text type="secondary">
                                                            {item.location}
                                                        </Text>
                                                    ) : null}
                                                    <Text type="secondary">
                                                        {item.creator_name}
                                                        {item.participants
                                                            ?.length
                                                            ? ` · ${item.participants
                                                                  .map(p => p.name)
                                                                  .join(', ')}`
                                                            : ''}
                                                    </Text>
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        ) : (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Нет событий"
                            />
                        )}
                    </Card>
                </div>
            </Space>

            <Modal
                title={editing ? 'Изменить' : 'Новая запись'}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                okText="Сохранить"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={save}
                >
                    <Form.Item
                        name="type"
                        label="Тип"
                        rules={[{ required: true }]}
                    >
                        <Radio.Group>
                            <Radio.Button value="meeting">
                                Встреча
                            </Radio.Button>
                            <Radio.Button value="event">
                                Событие
                            </Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item
                        name="title"
                        label="Название"
                        rules={[{ required: true, message: 'Укажите название' }]}
                    >
                        <Input placeholder="Например, созвон с клиентом" />
                    </Form.Item>
                    <Form.Item
                        name="all_day"
                        label="Весь день"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item
                        name="range"
                        label={allDay ? 'Даты' : 'Начало и окончание'}
                        rules={[{ required: true, message: 'Укажите время' }]}
                    >
                        <RangePicker
                            showTime={!allDay}
                            format={
                                allDay
                                    ? 'DD.MM.YYYY'
                                    : 'DD.MM.YYYY HH:mm'
                            }
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="participant_ids"
                        label="Сотрудники"
                    >
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Необязательно — можно только себе"
                            options={colleagues
                                .filter(u => Number(u.id) !== Number(me?.id))
                                .map(u => ({
                                    value: u.id,
                                    label: u.name
                                }))}
                        />
                    </Form.Item>
                    <Form.Item name="location" label="Место">
                        <Input placeholder="Офис, ссылка, адрес" />
                    </Form.Item>
                    <Form.Item name="description" label="Комментарий">
                        <TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            <style>{`
                @media (max-width: 900px) {
                    .calendar-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
                .crm-calendar .ant-picker-content thead th:nth-child(6),
                .crm-calendar .ant-picker-content thead th:nth-child(7) {
                    color: #cf1322;
                }
                .crm-calendar .crm-cal-weekend .ant-picker-calendar-date-value,
                .crm-calendar .crm-cal-holiday .ant-picker-calendar-date-value {
                    color: #cf1322;
                    font-weight: 600;
                }
                .crm-calendar .crm-cal-holiday {
                    background: #fff1f0;
                    border-radius: 4px;
                }
                .crm-calendar .crm-cal-short .ant-picker-calendar-date-value {
                    color: #d46b08;
                    font-weight: 600;
                }
                .crm-calendar .crm-cal-short {
                    background: #fff7e6;
                    border-radius: 4px;
                }
                .crm-calendar .crm-cal-work_weekend .ant-picker-calendar-date-value {
                    color: #389e0d;
                    font-weight: 600;
                }
                .crm-calendar .crm-cal-work_weekend {
                    background: #f6ffed;
                    border-radius: 4px;
                }
                .crm-calendar .crm-cal-chip {
                    font-size: 11px;
                    line-height: 1.2;
                    color: inherit;
                    opacity: 0.85;
                    margin-bottom: 2px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            `}</style>
            </ConfigProvider>
        </Layout>
    );
};

export default CalendarPage;
